import { HTTPErrorResponse } from 'dy-frontend-http-repository/lib/data/types';
import momentTz from 'moment-timezone';
import React, { useState } from 'react';

import { ToastUtils } from '@app/data/utils';
import { Button, Card, Divider, Intent } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import Overlay from '@components/Overlay';
import DevText from '@components/Text';
import { ModalProps } from '@modals/types';

import { scheduleFlexMonth } from '../../store/effects';

// Flex Month dates are computed server-side from the next billing instant; this preview
// replicates the same rules (Pacific calendar dates, +1 month clamped, +1 day)
const PACIFIC_TIMEZONE = 'America/Los_Angeles';

export interface ScheduleFlexMonthModalProps {
    subscriptionId: ID;
    nextBillingAt: string;
    refetchOnSuccess?: () => Promise<unknown>;
}

type Props = ModalProps<ScheduleFlexMonthModalProps>;

const ScheduleFlexMonthModal: React.FC<Props> = ({ closeModal, data }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!data) {
        closeModal?.();
        return null;
    }

    // Official pause start = next billing instant; dates below are Pacific calendar dates
    const pauseStartDate = momentTz.tz(data.nextBillingAt, PACIFIC_TIMEZONE).startOf('day');
    const resumeDate = pauseStartDate.clone().add(1, 'month').add(1, 'day');
    const cancellableUntil = momentTz.tz(data.nextBillingAt, PACIFIC_TIMEZONE).subtract(48, 'hours');

    const handleScheduleFlexMonth = async () => {
        setIsSubmitting(true);

        try {
            await scheduleFlexMonth(data.subscriptionId);

            ToastUtils.showToast({ message: 'Flex Month scheduled', intent: Intent.SUCCESS });

            if (data.refetchOnSuccess) {
                await data.refetchOnSuccess();
            }

            closeModal?.();
        } catch (e) {
            console.error(e);

            const response = (e as { response?: HTTPErrorResponse }).response;
            ToastUtils.showToast({
                message: response?.data?.message ?? 'Could not schedule Flex Month',
                intent: Intent.DANGER,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Overlay isOpen onClose={closeModal}>
            <Card style={{ width: '600px' }}>
                <Flex className="mb-2" align="center" justify="space-between">
                    <Heading type="h4">Pause subscription (Flex Month)</Heading>
                    <Button minimal icon="cross" onClick={closeModal} />
                </Flex>

                <DevText className="mb-2">
                    Billing will be paused for one month starting from the next billing date. Exact dates are computed
                    by the platform:
                </DevText>

                <div className="mb-1">
                    <DevText muted className="mb-small">
                        Pause starts (next billing date)
                    </DevText>
                    <DevText>{pauseStartDate.format('D MMM, YYYY')}</DevText>
                </div>

                <div className="mb-2">
                    <DevText muted className="mb-small">
                        Billing resumes
                    </DevText>
                    <DevText>{resumeDate.format('D MMM, YYYY')}</DevText>
                </div>

                <Divider className="mb-2" />

                <DevText muted className="mb-1">
                    The client will receive email notifications when the Flex Month is scheduled, shortly before the
                    pause starts and shortly before billing resumes.
                </DevText>

                <DevText muted className="mb-2">
                    The scheduled Flex Month can be cancelled until {cancellableUntil.format('D MMM, YYYY, HH:mm z')}{' '}
                    (48 hours before the pause starts).
                </DevText>

                <Flex justify="flex-end">
                    <Button className="mr-1" outlined onClick={closeModal} disabled={isSubmitting}>
                        Cancel
                    </Button>

                    <Button intent={Intent.PRIMARY} loading={isSubmitting} onClick={handleScheduleFlexMonth}>
                        Pause Subscription
                    </Button>
                </Flex>
            </Card>
        </Overlay>
    );
};

export default ScheduleFlexMonthModal;
